import { useState } from 'react'
import clsx from 'clsx'
import { Form, Formik, FormikHelpers } from 'formik'

/* blocks */
import Button from '@/atoms/Button/Button'
import TextField from '@/molecules/FormikFields/TextField'
import PhoneField from '@/molecules/FormikFields/PhoneField'
import SelectField from '@/molecules/FormikFields/SelectField'

/* assets */
import { ReactComponent as WaitlistBackground } from 'public/assets/influencer/icons/WaitlistBackground.svg'

/* utils */
import {
  FormValues,
  initialFormValues,
  validationSchema
} from './waitlistSchema'
import { numEmployeesOptions } from '@/utils/constants/form'

function WaitlistForm(): JSX.Element {
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (
    values: FormValues,
    helpers: FormikHelpers<FormValues>
  ) => {
    handlePardotSubmit(values)
    helpers.resetForm()
  }

  const handlePardotSubmit = async (values: FormValues) => {
    setSubmitting(true)
    try {
      const result = await fetch('/api/forms/resources/', {
        method: 'POST',
        body: JSON.stringify({
          ...values,
          url: 'https://go.pardot.com/l/1019212/2023-05-02/461x '
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      })

      const data = await result.json()

      if (result.ok) {
        setShowSuccessToast(true)
      } else {
        alert(data.message)
      }
      setSubmitting(false)
    } catch (_e) {
      alert('Please try again')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section
      className={clsx(
        'grid grid-cols-1 lg:grid-cols-12 | lg:gap-8 | py-12 lg:py-24'
      )}
    >
      <div className="lg:col-start-1 lg:col-end-6 | justify-items-center">
        <h1 className="text-[4rem] md:text-[5.25rem] leading-1 font-bold | mb-6 ">
          Join the Waitlist
        </h1>
        <p className="h4-mobile | mb-8">
          Learn from the most inspiring content marketeers, artists, leaders,
          and icons in the world.
        </p>
        <WaitlistBackground className="self-end hidden lg:block" />
      </div>

      <div className="lg:col-start-8 lg:col-end-13 | w-full | text-gray-70 | justify-items-end">
        <Formik<FormValues>
          initialValues={initialFormValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          <Form name="Waitlist Form" id="waitlist-form">
            {showSuccessToast && (
              <div className="bg-green-50 text-white |  border border-green-30 rounded-xl |  py-4 px-5 mb-5 | font-bold">
                <p className="text-paragraph-4">
                  You have been registered successfully
                </p>
              </div>
            )}
            <TextField
              name="firstName"
              className="mb-6"
              labelClass="text-white"
              label="First Name"
              placeholder="First Name"
              required
            />
            <TextField
              name="lastName"
              className="mb-6"
              labelClass="text-white"
              label="Last Name"
              placeholder="Last Name"
              required
            />
            <TextField
              name="email"
              className="mb-6"
              labelClass="text-white"
              label="Email Address"
              placeholder="example@peppercontent.in"
              required
            />
            <TextField
              name="company"
              className="mb-6"
              labelClass="text-white"
              label="Company Name"
              required
            />
            <TextField
              name="designation"
              className="mb-6"
              labelClass="text-white"
              label="Designation"
              required
            />
            <PhoneField
              name="phone"
              label="Contact Number"
              className="mb-6 relative z-10"
              required
            />
            <SelectField
              name="numEmployees"
              label="Number of Employees"
              placeholder="Select"
              options={numEmployeesOptions.map(item => ({
                name: item.name,
                value: item.value
              }))}
              className="relative mb-10"
              required
            />
            <Button
              className="w-full text-blue-50!"
              loading={submitting}
              size="large"
              htmlType="submit"
              type="tertiary"
            >
              Join the Waitlist
            </Button>
          </Form>
        </Formik>
      </div>
    </section>
  )
}

export default WaitlistForm
